import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router';
import { chatBannerStackAtom, ChatNotificationItem } from './chatNotificationStore';
import styles from './ChatNotificationStack.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

const AUTO_DISMISS_MS = 5000;

const truncate = (s: string, n = 60): string => (s.length > n ? s.slice(0, n) + '…' : s);

const previewContent = (item: ChatNotificationItem): string => {
    if (item.messageType === 'IMAGE') return '사진을 보냈습니다';
    if (item.messageType === 'LOCATION') return '위치를 공유했습니다';
    return truncate(item.content || '');
};

interface BannerProps {
    item: ChatNotificationItem;
    onDismiss: (id: string) => void;
    onTap: (item: ChatNotificationItem) => void;
    index: number;
}

const ChatBanner: React.FC<BannerProps> = ({ item, onDismiss, onTap, index }) => {
    const timerRef = useRef<number | null>(null);
    const touchStartYRef = useRef<number | null>(null);
    const touchStartXRef = useRef<number | null>(null);
    const [dragOffset, setDragOffset] = useState<number>(0);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        timerRef.current = window.setTimeout(() => onDismiss(item.id), AUTO_DISMISS_MS);
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, [item.id, onDismiss]);

    const cancelTimer = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        cancelTimer();
        touchStartYRef.current = e.touches[0].clientY;
        touchStartXRef.current = e.touches[0].clientX;
        setDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (touchStartYRef.current === null) return;
        const dy = e.touches[0].clientY - touchStartYRef.current;
        // 위로만 스와이프 시 banner 가 위로 사라짐. 아래로는 안 움직임.
        if (dy < 0) setDragOffset(dy);
    };

    const handleTouchEnd = () => {
        setDragging(false);
        if (dragOffset < -40) {
            onDismiss(item.id);
            return;
        }
        // 복귀 + 타이머 재시작
        setDragOffset(0);
        timerRef.current = window.setTimeout(() => onDismiss(item.id), AUTO_DISMISS_MS);
    };

    const handleClick = () => {
        cancelTimer();
        onTap(item);
    };

    const initial = (item.senderNickname || '?').charAt(0).toUpperCase();

    return (
        <div
            className={cls(`chat-banner ${dragging ? 'is-dragging' : ''}`)}
            style={{ transform: `translateY(${dragOffset}px)`, top: 12 + index * 8 }}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            role="button"
        >
            <div className={cls("chat-banner-avatar")}>
                {item.senderProfileUrl ? (
                    <img src={item.senderProfileUrl} alt="" />
                ) : (
                    <span>{initial}</span>
                )}
            </div>
            <div className={cls("chat-banner-body")}>
                <div className={cls("chat-banner-title")}>
                    <span className={cls("chat-banner-name")}>{item.senderNickname}</span>
                    {item.roomName && <span className={cls("chat-banner-room")}> · {item.roomName}</span>}
                </div>
                <div className={cls("chat-banner-preview")}>{previewContent(item)}</div>
            </div>
            <button
                type="button"
                className={cls("chat-banner-close")}
                aria-label="닫기"
                onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
            >
                ×
            </button>
        </div>
    );
};

export const ChatNotificationStack: React.FC = () => {
    const [banners, setBanners] = useAtom(chatBannerStackAtom);
    const navigate = useNavigate();

    const handleDismiss = (id: string) => {
        setBanners((prev) => prev.filter((b) => b.id !== id));
    };

    const handleTap = (item: ChatNotificationItem) => {
        setBanners((prev) => prev.filter((b) => b.id !== item.id));
        navigate(`/chat/room/${item.roomId}`);
    };

    if (banners.length === 0) return null;

    return (
        <div className={cls("chat-banner-stack")}>
            {banners.map((b, idx) => (
                <ChatBanner
                    key={b.id}
                    item={b}
                    index={idx}
                    onDismiss={handleDismiss}
                    onTap={handleTap}
                />
            ))}
        </div>
    );
};
